import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import DownloadIcon from '@mui/icons-material/Download';
import { format, addDays } from 'date-fns';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Assignment, Person, Task, Special } from '../types';
import config from '../config';

// Daily Report Configuration: Specify which varos to display and in what order
// Change this array to customize the columns shown in the Daily Report
const DAILY_REPORT_VAROS_ORDER = [
  // Add varo names here in the order you want them to appear in the Daily Report
  // Example:
  // 'Post Bandagi Ginan',
  // 'Tasbih',
  // 'Firman',
  // 'Ghatpat Dua',
  // 'Ghatpat Ginan',
  'First Ginan / Qasida',
  'Farman Before 4am',
  'Baitul Khayal Tasbih',
  'Bandagi Ginan',
  'Satada Tasbih',
  'Ruhani Visaal',
];

// Helper function to get varos in the configured order for Daily Report
const getDailyReportVarosOrder = (allTasks: Task[]): Task[] => {
  if (DAILY_REPORT_VAROS_ORDER.length > 0) {
    // Use configured order - create a map for quick lookup
    const varoMap = new Map(allTasks.map((task) => [task.name, task]));
    const sortedVaros = DAILY_REPORT_VAROS_ORDER.map((varoName) => varoMap.get(varoName)).filter((varo): varo is Task => varo !== undefined);
    // Add any varos not in the config list at the end (alphabetically sorted)
    const configuredNames = new Set(DAILY_REPORT_VAROS_ORDER);
    const unconfiguredVaros = allTasks
      .filter((task) => !configuredNames.has(task.name))
      .sort((a, b) => a.name.localeCompare(b.name));
    return [...sortedVaros, ...unconfiguredVaros];
  } else {
    // If no configuration, use alphabetical order
    return [...allTasks].sort((a, b) => a.name.localeCompare(b.name));
  }
};

const ReportsManager = (): JSX.Element => {
  const [startDate, setStartDate] = useState<string>(() => {
    const cached = sessionStorage.getItem('reportsManagerStartDate');
    if (cached) return cached;
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  });
  const [endDate, setEndDate] = useState<string>(() => {
    const cached = sessionStorage.getItem('reportsManagerEndDate');
    if (cached) return cached;
    const now = new Date();
    return format(now, 'yyyy-MM-dd');
  });
  const [reportType, setReportType] = useState('daily');
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [specials, setSpecials] = useState<Special[]>([]);
  const [peopleSortBy, setPeopleSortBy] = useState<'total' | 'name'>(
    'total'
  );
  const [peopleSortOrder, setPeopleSortOrder] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    fetchData();
  }, [startDate, endDate, reportType]);

  const fetchData = async () => {
    try {
      const assignments = [];
      let currentDate = new Date(startDate + 'T00:00:00');
      const end = new Date(endDate + 'T00:00:00');
      
      while (currentDate <= end) {
        const formattedDate = format(currentDate, 'yyyy-MM-dd');
        
        console.log('Fetching data for date:', {
          currentDate: currentDate.toLocaleString(),
          formattedDate,
        });

        const assignmentsRes = await axios.get(`${config.api.baseUrl}/assignments`, {
          params: { date: formattedDate },
          withCredentials: true,
        });
        
        assignments.push(...assignmentsRes.data);
        currentDate = addDays(currentDate, 1);
      }

      const [peopleRes, tasksRes, specialsRes] = await Promise.all([
        axios.get(`${config.api.baseUrl}/people`, {
          withCredentials: true,
        }),
        axios.get(`${config.api.baseUrl}/tasks`, {
          withCredentials: true,
        }),
        axios.get(`${config.api.baseUrl}/specials`, {
          withCredentials: true,
        }),
      ]);

      setAssignments(assignments);
      setPeople(peopleRes.data);
      setTasks(tasksRes.data);
      setSpecials(specialsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleStartDateChange = (newDate: Date | null) => {
    if (newDate) {
      const formatted = format(newDate, 'yyyy-MM-dd');
      setStartDate(formatted);
      sessionStorage.setItem('reportsManagerStartDate', formatted);
    }
  };

  const handleEndDateChange = (newDate: Date | null) => {
    if (newDate) {
      const formatted = format(newDate, 'yyyy-MM-dd');
      setEndDate(formatted);
      sessionStorage.setItem('reportsManagerEndDate', formatted);
    }
  };

  const getFilteredData = () => {
    const filteredAssignments = assignments.filter((assignment) => {
      const assignmentDate = assignment.startDate.slice(0, 10);
      return assignmentDate >= startDate && assignmentDate <= endDate;
    });

    // Map date (yyyy-MM-dd) -> special name from Special table (ignore Assignment.special)
    const specialsByDate: Record<string, string> = {};
    specials.forEach((s) => {
      const d = format(new Date(s.date), 'yyyy-MM-dd');
      specialsByDate[d] = s.name;
    });

    switch (reportType) {
      case 'assignments':
        return filteredAssignments.map((assignment) => {
          const dateStr = format(new Date(assignment.startDate), 'yyyy-MM-dd');
          return {
            Date: dateStr,
            'Day of Week': format(new Date(assignment.startDate), 'EEEE'),
            Special: specialsByDate[dateStr] ?? '',
            Varo: assignment.varos.name,
            Person: assignment.person.name,
            Phone: assignment.person.phone || '',
            Email: assignment.person.email || '',
          };
        });

      case 'people': {
        let peopleRows = people
          .map((person) => {
            const personAssignments = filteredAssignments.filter((a) => a.personId === person.id);

            // Remove people in report that are not assigned for the date range
            if (personAssignments.length < 1) return null; // filter out here
            
            // Split name into first and last
            const nameParts = person.name.trim().split(' ');
            const lastName = nameParts.length > 1 ? nameParts[nameParts.length - 1] : nameParts[0];
            const firstName = nameParts.slice(0, -1).join(' ');
            const displayName = firstName ? `${lastName}, ${firstName}` : lastName;
            return {
              Name: displayName,
              Email: person.email || '',
              Phone: person.phone || '',
              Ranking: person.ranking || '',
              'Total Assignments': personAssignments.length,
              Skills: person.varos?.map((pv) => pv.varos.name).join(', ') || '',
              'Assignment Dates': personAssignments
                .map((a) => format(new Date(a.startDate), 'yyyy-MM-dd'))
                .join(', '),
              // _sortLastName and _sortFirstName are only for sorting, not for display
              _sortLastName: lastName.toLowerCase(),
              _sortFirstName: firstName.toLowerCase(),
            };
          })
          // Not only are we filtering out null, but the result is definitely non-null.
          .filter((p): p is NonNullable<typeof p> => p !== null);
          
        
        // Sorting logic
        if (peopleSortBy === 'total') {
          peopleRows = peopleRows.sort((a, b) =>
            peopleSortOrder === 'asc'
              ? a['Total Assignments'] - b['Total Assignments']
              : b['Total Assignments'] - a['Total Assignments']
          );
        } else if (peopleSortBy === 'name') {
          peopleRows = peopleRows.sort((a, b) => {
            const last = a._sortLastName.localeCompare(b._sortLastName);
            if (last !== 0) return peopleSortOrder === 'asc' ? last : -last;
            const first = a._sortFirstName.localeCompare(b._sortFirstName);
            return peopleSortOrder === 'asc' ? first : -first;
          });
        }
        // Remove the _sortLastName and _sortFirstName before returning for display
        return peopleRows.map(({ _sortLastName, _sortFirstName, ...rest }) => rest);
      }

      case 'tasks':
        return tasks.map((task) => {
          const taskAssignments = filteredAssignments.filter((a) => a.varosId === task.id);
          return {
            'Varo Name': task.name,
            Description: task.description || '',
            'Total Assignments': taskAssignments.length,
            'Unique Murids': new Set(taskAssignments.map((a) => a.personId)).size,
            'Assignment Dates': taskAssignments
              .map((a) => format(new Date(a.startDate), 'yyyy-MM-dd'))
              .join(', '),
          };
        });

      case 'daily': {
        // Daily Report format
        // Structure: Day of Week | Date | Varo1 | Varo2 | Varo3 | ... | VaroN
        // Each row represents one date, columns show person assigned to each varo
        
        // Get all unique dates in range
        const dateSet = new Set<string>();
        filteredAssignments.forEach((assignment) => {
          const assignmentDate = assignment.startDate.slice(0, 10);
          if (assignmentDate >= startDate && assignmentDate <= endDate) {
            dateSet.add(assignmentDate);
          }
        });
        
        // Sort dates
        const sortedDates = Array.from(dateSet).sort();
        
        // Get varos in the configured order
        const sortedVaros = getDailyReportVarosOrder(tasks);
        
        // Build rows
        const rows = sortedDates.map((dateStr) => {
          const date = new Date(dateStr + 'T00:00:00');
          const row: Record<string, string> = {
            'Day of Week': format(date, 'EEEE'),
            'Date': format(date, 'yyyy-MM-dd'),
          };
          
          // For each varo, find the person assigned on this date
          sortedVaros.forEach((varo) => {
            const assignment = filteredAssignments.find(
              (a) =>
                a.varosId === varo.id &&
                a.startDate.slice(0, 10) === dateStr
            );
            row[varo.name] = assignment ? assignment.person.name : '';
          });
          
          return row;
        });
        
        return rows;
      }

      default:
        return [];
    }
  };

  const handleExportToExcel = () => {
    const data = getFilteredData();
    const ws = XLSX.utils.json_to_sheet(data);

    // Auto-fit columns
    const colWidths: number[] = [];
    const maxWidth = 50;
    const padding = 2;
    
    if (data.length > 0) {
      const headers = Object.keys(data[0]);
      headers.forEach((header, colIndex) => {
        colWidths[colIndex] = header.length + padding;
      });
    }

    data.forEach((row) => {
      Object.values(row).forEach((value, colIndex) => {
        const cellValue = String(value || '');
        colWidths[colIndex] = Math.min(
          maxWidth,
          Math.max(colWidths[colIndex], cellValue.length + padding),
        );
      });
    });

    ws['!cols'] = colWidths.map((width) => ({ width }));

    // Make header row bold
    if (data.length > 0) {
      const headers = Object.keys(data[0]);
      const headerRowIndex = 1; // Row 1 in Excel (0-indexed row + 1)
      headers.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: colIndex });
        if (!ws[cellAddress]) return;
        
        // Set bold style for header cells
        ws[cellAddress].s = {
          font: { bold: true },
        };
      });
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, reportType);

    const fileName = `${reportType.toLowerCase()}_report_${startDate}_to_${endDate}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Reports
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>Report Type</InputLabel>
            <Select
              value={reportType}
              label="Report Type"
              onChange={(e) => setReportType(e.target.value)}
            >
              <MenuItem value="assignments">Assignments Report</MenuItem>
              <MenuItem value="people">Murid Report</MenuItem>
              <MenuItem value="tasks">Varos Report</MenuItem>
              <MenuItem value="daily">Daily Report</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2} alignItems="center">
            <DatePicker
              label="Start Date"
              value={startDate ? new Date(startDate + 'T00:00:00') : null}
              onChange={handleStartDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />
            <Typography>to</Typography>
            <DatePicker
              label="End Date"
              value={endDate ? new Date(endDate + 'T00:00:00') : null}
              onChange={handleEndDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                },
              }}
            />
          </Stack>

          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleExportToExcel}
            disabled={endDate < startDate}
          >
            Export to Excel
          </Button>
        </Stack>
      </Paper>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {reportType === 'assignments' && (
                <>
                  <TableCell>Date</TableCell>
                  <TableCell>Day of Week</TableCell>
                  <TableCell>Special</TableCell>
                  <TableCell>Varo</TableCell>
                  <TableCell>Murid</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Email</TableCell>
                </>
              )}
              {reportType === 'people' && (
                <>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => {
                        if (peopleSortBy === 'name') {
                          setPeopleSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setPeopleSortBy('name');
                          setPeopleSortOrder('asc');
                        }
                      }}
                      endIcon={
                        peopleSortBy === 'name' ? (
                          peopleSortOrder === 'asc' ? '▲' : '▼'
                        ) : null
                      }
                    >
                      Name
                    </Button>
                  </TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Phone</TableCell>
                  <TableCell>Ranking</TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      onClick={() => {
                        if (peopleSortBy === 'total') {
                          setPeopleSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
                        } else {
                          setPeopleSortBy('total');
                          setPeopleSortOrder('asc');
                        }
                      }}
                      endIcon={
                        peopleSortBy === 'total' ? (
                          peopleSortOrder === 'asc' ? '▲' : '▼'
                        ) : null
                      }
                    >
                      Total Assignments
                    </Button>
                  </TableCell>
                  <TableCell>Skills</TableCell>
                </>
              )}
              {reportType === 'tasks' && (
                <>
                  <TableCell>Varo Name</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Total Assignments</TableCell>
                  <TableCell>Unique Murids</TableCell>
                </>
              )}
              {reportType === 'daily' && (() => {
                const sortedVaros = getDailyReportVarosOrder(tasks);
                return (
                  <>
                    <TableCell sx={{ minWidth: 90}}>Day of Week</TableCell>
                    <TableCell sx={{ minWidth: 80 }}>Date</TableCell>
                    {sortedVaros.map((varo) => (
                      <TableCell key={varo.id} sx={{ minWidth: 100 }}>{varo.name}</TableCell>
                    ))}
                  </>
                );
              })()}
            </TableRow>
          </TableHead>
          <TableBody>
            {getFilteredData().map((row, index) => (
              <TableRow key={index}>
                {Object.values(row).map((value, cellIndex) => {
                  // Apply width styling for Daily Report columns
                  let cellStyle = {};
                  if (reportType === 'daily') {
                    if (cellIndex === 0) {
                      // Day of Week column
                      cellStyle = { minWidth: 50 };
                    } else if (cellIndex === 1) {
                      // Date column
                      cellStyle = { minWidth: 50 };
                    } else {
                      // Varo columns (names)
                      cellStyle = { minWidth: 150 };
                    }
                  }
                  return (
                    <TableCell key={cellIndex} sx={cellStyle}>{value}</TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default ReportsManager;
